import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { flexRender, Header } from "@tanstack/react-table";
import { CSSProperties } from "react";
import { $gray200, $sky60 } from "../../../../cssjs/colors";
import { FlightTable } from "../../../../enum/flights/FlightTableEnum";
import { getSortTitle } from "../../../../utils/flights/FlightDetailHelperFunctions";
import SortIconSelector from "../../../common/icon-selectors/SortIconSelector";
import { ColumnFilter } from "./ColumnFilter";
const columnResizeDirection = "ltr";

interface IDraggableTableHeaderProps {
  header: Header<FlightTable, unknown>;
  isColumnFilterActive: boolean | undefined;
  isColumnSortActive: boolean | undefined;
}

export const DraggableTableHeader: React.FC<IDraggableTableHeaderProps> = ({
  header,
  isColumnFilterActive,
  isColumnSortActive,
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });
  const styles = useStyles();

  const thStyle: CSSProperties = {
    opacity: isDragging ? 0.6 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: "width transform 0.2s ease-in-out",
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
    padding: "8px 8px",
  };

  const pointerStyle = isDragging ? { cursor: "grabbing" } : { cursor: "grab" };

  return (
    <th
      key={header.id}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={thStyle}
    >
      <div className="d-flex" data-testid="column-header-label">
        {header.isPlaceholder ? null : (
          <div
            className={mergeClasses(styles.th, "fs-16", "pa-sm-2")}
            {...attributes}
            {...listeners}
            style={{
              ...pointerStyle,
              color: isColumnFilterActive ? $sky60 : "black",
            }}
            data-testid="column-drag-button"
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        )}
        <div
          id="resizer"
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`${columnResizeDirection} ${styles.resizer} ${
            header.column.getIsResizing() ? "isResizing" : ""
          }`}
        />
        <div
          onClick={header.column.getToggleSortingHandler()}
          className={mergeClasses(
            header.column.getCanSort() ? "cursor-pointer" : "",
            "d-flex"
          )}
          title={getSortTitle(
            header.column.getCanSort(),
            header.column.getNextSortingOrder()
          )}
          data-testid="column-sort-button"
          style={{
            color: isColumnSortActive ? $sky60 : "black",
          }}
        >
          <SortIconSelector sortValue={header.column.getIsSorted()} />
        </div>
      </div>
      {header.column.getCanFilter() ? (
        <ColumnFilter column={header.column} />
      ) : null}
    </th>
  );
};

const useStyles = makeStyles({
  th: {
    padding: "0px 10px",
    textAlign: "left",
    position: "relative",
    minHeight: "30px",
  },
  resizer: {
    position: "absolute",
    top: "0",
    height: "100%",
    opacity: 0.4,
    width: "1px",
    background: `${$gray200}`,
    cursor: "col-resize",
    userSelect: "none",
    touchAction: "none",
    "&.ltr": {
      right: 0,
    },
    "&.rtl": {
      left: 0,
    },
    "&.isResizing": {
      background: `${$gray200}`,
      width: "5px",
      opacity: 1,
    },
    "&:hover": {
      background: `${$gray200}`,
      width: "5px",
      opacity: 1,
    },
  },
});
